
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { RotateCcw } from 'lucide-react';
import { useConnectionQuality } from '@/hooks/useConnectionQuality';
import { useOnlineStatus, retryOperation, isNetworkError } from '@/utils/connectionUtils';
import { useIntersectionObserver } from '@/utils/optimizationUtils';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Button } from "@/components/ui/button";
import PaymentListTable from './PaymentListTable';
import PaymentListHeader from './PaymentListHeader';
import ManualPaymentDialog from './ManualPaymentDialog';
import { Family } from '@/types/models';
import { FamilyService } from '@/services/familyServices/familyService';

interface Payment {
  id: string;
  amount_cents: number;
  family_id: string;
  family_name: string;
  receipt_url: string | null;
  paid_at: string;
  payment_method?: string;
  check_number?: string;
  receipt_number?: string;
  status?: string;
}

const RecentPaymentsTable = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [families, setFamilies] = useState<Family[]>([]);
  const [loadingFamilies, setLoadingFamilies] = useState(false);
  const isSlowConnection = useConnectionQuality().isSlowConnection;
  const isOnline = useOnlineStatus();
  
  // Determine the appropriate page size based on connection quality
  const pageSize = useMemo(() => 
    isSlowConnection ? 5 : 20, 
    [isSlowConnection]
  );

  // Implement intersection observer to only load data when component is visible
  const [ref, isVisible] = useIntersectionObserver(0.1);
  
  const fetchRecentPayments = useCallback(async () => {
    if (!isOnline) {
      setError('You are currently offline. Please check your connection and try again.');
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      const organizationId = session?.user?.user_metadata?.organization_id;

      if (!organizationId && import.meta.env.MODE !== 'development') {
        console.warn("❌ No organizationId found for current user.");
        setPayments([]);
        setIsLoading(false);
        return;
      }

      // Use retry operation utility for more resilient data fetching
      const data = await retryOperation(async () => {
        const query = supabase
          .from('payments')
          .select(`
            id,
            amount_cents,
            family_id,
            paid_at,
            receipt_url,
            payment_method,
            check_number,
            receipt_number,
            status,
            families!inner(name)
          `)
          .order('paid_at', { ascending: false })
          .limit(pageSize);

        if (import.meta.env.MODE !== 'development') {
          query.eq('organization_id', organizationId);
        }

        const { data, error: queryError } = await query;
        
        if (queryError) throw queryError;
        return data;
      }, isSlowConnection ? 2 : 3, isSlowConnection ? 2000 : 1000);
      
      const transformedData = (data || []).map(item => ({
        id: item.id,
        amount_cents: item.amount_cents,
        family_id: item.family_id,
        family_name: item.families?.name || 'Unnamed Family',
        receipt_url: item.receipt_url,
        paid_at: item.paid_at,
        payment_method: item.payment_method,
        check_number: item.check_number,
        receipt_number: item.receipt_number,
        status: item.status
      }));

      setPayments(transformedData);
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      
      if (isNetworkError(err)) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to load payment data: ' + (err.message || 'Unknown error'));
      }
      
      toast.error('Failed to load payments', {
        description: isNetworkError(err) ? 'Network error. Please check your connection.' : 'An unexpected error occurred.'
      });
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, [isOnline, pageSize, isSlowConnection]);

  // Fetch data when component mounts OR becomes visible
  React.useEffect(() => {
    // Always fetch on mount, or when becoming visible
    if (isVisible || payments.length === 0) {
      const timer = setTimeout(() => {
        fetchRecentPayments();
      }, isSlowConnection ? 300 : 0); // Add small delay for slow connections
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [fetchRecentPayments, isVisible, isSlowConnection, payments.length]);

  // Also fetch immediately on mount regardless of visibility
  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchRecentPayments();
    }, 100); // Small delay to avoid race conditions
    
    return () => clearTimeout(timer);
  }, []); // Only run on mount

  // Load families when component mounts
  React.useEffect(() => {
    const loadFamilies = async () => {
      const organizationId = user?.user_metadata?.organization_id;
      if (!organizationId) return;
      
      setLoadingFamilies(true);
      try {
        const familiesData = await FamilyService.getFamilies(organizationId);
        setFamilies(familiesData);
      } catch (error) {
        console.error('Failed to load families:', error);
      } finally {
        setLoadingFamilies(false);
      }
    };

    loadFamilies();
  }, [user?.user_metadata?.organization_id]);


  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    fetchRecentPayments();
  }, [fetchRecentPayments]);

  const handleRecordPayment = () => {
    setIsDialogOpen(true);
  };

  const handlePaymentCreated = () => {
    fetchRecentPayments(); // Refresh the data
  };

  // Mock handlers for PaymentListHeader compatibility
  const pageSizeOptions = [5, 10, 20];
  const handlePageSizeChange = () => {
    // Recent payments don't need pagination controls, but header expects this
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <PaymentListHeader 
          refreshing={true}
          isOnline={isOnline}
          pageSizeOptions={pageSizeOptions}
          pageSize={20}
          onRefresh={fetchRecentPayments}
          onPageSizeChange={handlePageSizeChange}
          onRecordPayment={handleRecordPayment}
        />
        <div className="space-y-2">
          {Array.from({ length: isSlowConnection ? 3 : 5 }).map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PaymentListHeader 
          refreshing={isRetrying}
          isOnline={isOnline}
          pageSizeOptions={pageSizeOptions}
          pageSize={20}
          onRefresh={fetchRecentPayments}
          onPageSizeChange={handlePageSizeChange}
          onRecordPayment={handleRecordPayment}
        />
        <div className="rounded-md border p-4 bg-red-50">
          <div className="text-red-500 mb-2">{error}</div>
          <Button 
            onClick={handleRetry}
            disabled={isRetrying || !isOnline}
            variant="outline"
            size="sm"
            className="bg-red-100 hover:bg-red-200 text-red-700"
          >
            {isRetrying ? 'Retrying...' : (
              <>
                <RotateCcw className="h-4 w-4 mr-2" />
                Try Again
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="space-y-4">
      <PaymentListHeader 
        refreshing={isRetrying}
        isOnline={isOnline}
        pageSizeOptions={pageSizeOptions}
        pageSize={20}
        onRefresh={fetchRecentPayments}
        onPageSizeChange={handlePageSizeChange}
        onRecordPayment={handleRecordPayment}
      />
      
      <PaymentListTable payments={payments} />
      
      <ManualPaymentDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        families={families}
        organizationId={user?.user_metadata?.organization_id || ''}
        onPaymentCreated={handlePaymentCreated}
      />
    </div>
  );
};

export default React.memo(RecentPaymentsTable);
